import React, { useEffect, useState } from "react";
import { X, Download, Mail, Users, Clock, Search } from "lucide-react";
import { toast } from "react-toastify";
import { listEventRegistrations } from "../../../../services/eventLandingServices";

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    return `${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} · ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  } catch {
    return String(d);
  }
};

const escapeCsv = (v) => {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const EventRegistrationsModal = ({ event, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!event) return;
    const fetchItems = async () => {
      setLoading(true);
      try {
        const res = await listEventRegistrations(event.id || event._id);
        setItems(res.data || []);
      } catch (err) {
        console.error("Failed to load registrations", err);
        toast.error("Failed to load registrations");
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [event]);

  const filtered = items.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const fd = s.formData || {};
    return (
      (fd.name || s.participantName || "").toLowerCase().includes(q) ||
      (fd.email || "").toLowerCase().includes(q) ||
      (fd.company || "").toLowerCase().includes(q)
    );
  });

  const exportCsv = () => {
    const rows = [
      ["Date", "Name", "Email", "Company", "Phone"].join(","),
      ...filtered.map((s) => {
        const fd = s.formData || {};
        return [
          escapeCsv(formatDate(s.createdAt || s.submittedAt)),
          escapeCsv(fd.name || s.participantName || ""),
          escapeCsv(fd.email || ""),
          escapeCsv(fd.company || ""),
          escapeCsv(fd.phone || ""),
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registrations-${event?.landingPageSlug || "event"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!event) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="font-sans bg-[#0a1a3a] border border-white/10 rounded-2xl w-full max-w-4xl my-8 shadow-2xl shadow-purple-900/20 max-h-[calc(100vh-4rem)] overflow-y-auto">
        {/* Header */}
        <div className="px-7 pt-6 pb-5 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-400" />
              <h2 className="text-2xl font-bold text-white">Registrations</h2>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              {event.yourName || "Event"} · {filtered.length}{" "}
              {filtered.length === 1 ? "registrant" : "registrants"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-7 py-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between border-b border-white/5">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, company..."
              className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* Body */}
        <div className="p-7">
          {loading && (
            <div className="text-center text-gray-400 py-12">Loading registrations…</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-300 text-lg">No registrations yet</p>
              <p className="text-gray-500 text-sm mt-1">
                {search
                  ? "Try a different search."
                  : "Share the landing page link to start collecting sign-ups."}
              </p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-gray-300">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Name</th>
                    <th className="text-left px-4 py-3 font-medium">Email</th>
                    <th className="text-left px-4 py-3 font-medium">Company</th>
                    <th className="text-left px-4 py-3 font-medium">Registered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filtered.map((s) => {
                    const fd = s.formData || {};
                    return (
                      <tr key={s._id || s.id} className="hover:bg-white/5">
                        <td className="px-4 py-3 text-white">
                          {fd.name || s.participantName || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {fd.email || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-300">
                          {fd.company || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDate(s.createdAt || s.submittedAt)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRegistrationsModal;
